/**
 * TL4K Design Tokens
 *
 * Design system condiviso per tutte le applicazioni TL4K.
 * Basato su: WCAG 2.2 AA, Legge Stanca (L. 4/2004), DM 166/2025,
 * European Accessibility Act, research IDC '25 (Interaction Design & Children).
 *
 * Segmentazione pedagogica:
 * - 4-6 anni: icone letterali, feedback sonori, target 60px
 * - 7-10 anni: gamification, lode contestuale, target 48px
 * - 11-14 anni: pensiero critico, AI copilota, target 44px
 */

// ─── Age Groups ───────────────────────────────────────────────

export type AgeGroup = "4-6" | "7-10" | "11-14";

export const AGE_GROUPS: readonly AgeGroup[] = ["4-6", "7-10", "11-14"];

// ─── Colors — Child-friendly palette (warm, accessible) ──────
// Tutti i colori testati per contrasto ≥4.5:1 su bianco (WCAG AA)

export const colors = {
  // Primary — blu professionale per app di scrittura/storytelling
  primary: {
    50: "#E6F4F9",
    100: "#B3D9EF",
    200: "#80BFE5",
    300: "#4DA5DB",
    400: "#2689C9",
    500: "#0076a8", // primary base
    600: "#006A97",
    700: "#005D86",
    800: "#005075",
    900: "#004364",
  },

  // Secondary — arancione caldo per accent e CTAs
  secondary: {
    50: "#FFF5E6",
    100: "#FFE0B3",
    200: "#FFCC80",
    300: "#FFB84D",
    400: "#FFA31A",
    500: "#f39200", // secondary base
    600: "#E08500",
    700: "#CC7700",
    800: "#B36900",
    900: "#995B00",
  },

  // Tertiary — terzo colore per palette completa
  tertiary: {
    50: "#FFF0E6",
    100: "#FFD6B3",
    200: "#FFBC80",
    300: "#FFA24D",
    400: "#FF881A",
    500: "#f18500", // tertiary base
    600: "#E07800",
    700: "#CC6A00",
    800: "#B35C00",
    900: "#994E00",
  },

  // Status — semantici, riconoscibili anche da bambini
  success: "#2E7D32", // verde scuro (contrasto 4.6:1 su bianco)
  warning: "#E65100", // arancione scuro (contrasto 4.5:1)
  error: "#C62828", // rosso scuro (contrasto 5.4:1)
  info: "#1565C0", // blu scuro (contrasto 5.8:1)

  // Status surface — sfondo chiaro abbinato al colore semantico
  successSurface: "#E8F5E9", // verde tenue
  warningSurface: "#FFF3E0", // arancione tenue
  errorSurface: "#FFEBEE", // rosso tenue
  infoSurface: "#E3F2FD", // blu tenue

  // Neutrals — per testi e sfondi
  neutral: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#8a8685",
    600: "#6e6a69",
    700: "#4a4949", // testo primario
    800: "#212121",
    900: "#1A1A1A",
  },

  // Surface — sfondi app (warm tone per comfort di lettura)
  surface: {
    default: "#FFFFFF",
    warm: "#f9f6f5",
    neutral: "#f2eeed",
    cool: "#e5e1e0",
    containerLow: "#ffffff",
    containerHigh: "#e5e1e0",
  },

  // Focus — indicatore di focus visibile (accessibilità motoria)
  focus: "#1565C0",
  focusRing: "0 0 0 3px rgba(21, 101, 192, 0.4)",

  // Outline — per bordi e separatori
  outline: "#8a8685",
  outlineVariant: "#d1cdcc",
} as const;

// ─── Spacing — base 4px (children need larger targets) ──────

export const spacing = {
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  "2xl": "32px",
  "3xl": "48px",
  "4xl": "64px",
} as const;

// ─── Shell chrome (TechyWeb header/footer + embedded viewport) ─
// Ancora: logo TechLAB4Kids / pulsante Techy (32px) + spacing.xs (4px) sopra/sotto.
export const shellChrome = {
  height: 40,
  heightPx: "40px",
  anchorSize: 32,
  utilityButtonHeight: 32,
  utilityButtonHeightPx: "32px",
  // Pulsanti utility con etichetta: larghezza minima touch (docs/TL4K_APP_STANDARD.md §8).
  // Quelli solo icona restano quadrati utilityButtonHeight × utilityButtonHeight.
  utilityButtonMinWidth: 44,
  utilityButtonFontSize: "12px",
  utilityIconSize: 16,
  verticalPadding: 4,
} as const;

// ─── Layout Viewport — compact spacing for 768px fit ─────────
// These tokens help apps fit within the TechyWeb embedded viewport
// (688px at 1366×768 reference) without scrolling.

export const layoutViewport = {
  // Vertical padding: compact for embedded, comfortable standalone
  embeddedPaddingY: "12px",    // py-3 equivalent
  standalonePaddingY: "16px",  // py-4 equivalent
  // Gaps between sections
  compactGap: "8px",           // gap-2 equivalent
  normalGap: "12px",           // gap-3 equivalent
  // Info/actions strip (single row: PIN + name + save + tabs)
  infoStripHeight: 36,
  infoStripHeightPx: "36px",
  // Toolbar row (segmented controls, tool selectors)
  toolbarHeight: 32,
  toolbarHeightPx: "32px",
} as const;

// ─── Touch Targets — per fascia età (WCAG + child-specific) ─
// Nota: i pulsanti compatti di utility nell'header (Admin, Schermo intero, join PIN)
// usano 32px di altezza come convenzione condivisa (vedi docs/TL4K_APP_STANDARD.md §8).
// I target sotto sono i MINIMI WCAG per controlli primari/standalone.

export const touchTargets = {
  "4-6": {
    minSize: "60px", // > WCAG, per motricità fine in sviluppo
    iconStyle: "literal" as const, // casa = Home, stella = preferito
    labelPosition: "below" as const,
    feedback: "multisensory" as const, // visivo + sonoro
  },
  "7-10": {
    minSize: "48px", // WCAG AAA
    iconStyle: "semi-literal" as const, // icona + label breve
    labelPosition: "inline" as const,
    feedback: "visual" as const,
  },
  "11-14": {
    minSize: "44px", // WCAG AA minimo
    iconStyle: "abstract" as const, // icone standard UI
    labelPosition: "inline" as const,
    feedback: "visual" as const,
  },
} as const;

// ─── Typography — leggibile per bambini ─────────────────────
// Font: Plus Jakarta Sans (headlines), Lexend (body text)

export const typography = {
  fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyHeadline: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyBody: '"Lexend", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", monospace',

  // Size scale per fascia età
  "4-6": {
    heading: "28px",
    subheading: "22px",
    body: "20px",
    small: "18px",
    button: "18px",
    lineHeight: "1.5",
  },
  "7-10": {
    heading: "24px",
    subheading: "20px",
    body: "18px",
    small: "16px",
    button: "16px",
    lineHeight: "1.5",
  },
  "11-14": {
    heading: "22px",
    subheading: "18px",
    body: "16px",
    small: "14px",
    button: "15px",
    lineHeight: "1.6",
  },
} as const;

// ─── Border Radius — morbido, child-friendly ────────────────

export const borderRadius = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "24px",
  DEFAULT: "1rem",
  pill: "2rem",
  "3xl": "3rem",
  full: "9999px",
} as const;

// ─── Shadows — leggere, non distrarre ───────────────────────

export const shadows = {
  sm: "0 1px 2px rgba(0, 0, 0, 0.05)",
  md: "0 4px 6px rgba(0, 0, 0, 0.07)",
  lg: "0 10px 15px rgba(0, 0, 0, 0.1)",
  xl: "0 20px 25px rgba(0, 0, 0, 0.1)",
} as const;

// ─── Transitions — non troppo veloci (bambini più lenti) ────

export const transitions = {
  fast: "150ms ease",
  base: "250ms ease",
  slow: "350ms ease", // preferibile per 4-6 anni
} as const;

// ─── Z-Index — livelli standard ─────────────────────────────

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  overlay: 300,
  modal: 400,
  popover: 500,
  toast: 600,
} as const;

// ─── Compliance Constants ───────────────────────────────────

export const compliance = {
  // WCAG 2.2 AA
  minContrastText: 4.5, // rapporto minimo testo/sfondo
  minContrastGraphics: 3, // rapporto minimo elementi grafici
  minTouchTarget: 44, // px (WCAG AA)
  minTouchTargetEnhanced: 48, // px (WCAG AAA)

  // Child-specific (research-based)
  minTouchTarget4to6: 60, // px per 4-6 anni
  maxWordsPerSentence4to6: 10, // frasi brevi
  maxWordsPerSentence7to10: 15,
  maxWordsPerSentence11to14: 25,

  // Normative italiane
  parentalGateRequired: true, // DM 166/2025
  emotionRecognitionProhibited: true, // AI Act + DM 166/2025
  socialScoringProhibited: true,
  dataMinimization: true, // GDPR Art. 5
  geolocationDefaultOff: true,
} as const;

// ─── Helper Functions ────────────────────────────────────────

export function getTouchTargetSize(ageGroup: AgeGroup): string {
  return touchTargets[ageGroup].minSize;
}

export function getTypography(ageGroup: AgeGroup, variant: keyof typeof typography["4-6"]): string {
  return typography[ageGroup][variant];
}

export function isContrastCompliant(foreground: string, background: string): boolean {
  // Semplificato: in produzione usare libreria calcolo contrasto
  // Qui validiamo solo colori noti del design system
  const knownHighContrast: [string, string][] = [
    [colors.neutral[800], colors.surface.default],
    [colors.neutral[800], colors.surface.warm],
    [colors.primary[500], colors.surface.default],
    [colors.success, colors.surface.default],
    [colors.error, colors.surface.default],
    [colors.info, colors.surface.default],
  ];
  return knownHighContrast.some(([fg, bg]) => fg === foreground && bg === background);
}
